.container {
  display: flex;
  flex-direction: column;
  height: 100%;
  /* Auto-scales between popup (360px) and sidepanel (380px)
   * containers — rows use flex with `justify-content: space-between`
   * so the controls push to the right edge regardless of container
   * width. */
  width: 100%;
}

/* Header — chevron back button + title. Per the refreshed Figma
 * spec (node 1108:2605): 12px vertical / 16px horizontal padding,
 * 2px gap between the IconButton and the title, bottom border
 * divider. */
.header {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-12) var(--spacing-16);
  border-bottom: 1px solid var(--stroke-neutral);
}

/* Reset the default margin <Text as="h2"> inherits from UA styles. */
.title {
  margin: 0;
}

.content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  /* Top padding 12px per Figma (node 1108:2608). No horizontal
   * padding on the wrapper — each row owns its own 16px inset so
   * section dividers can span edge-to-edge. */
  padding-top: var(--spacing-12);
}

.section {
  display: flex;
  flex-direction: column;
}

/* Section headers (Appearance, Notifications, Connection, About)
 * — tertiary text, semibold body, 16px horizontal inset, 12px
 * vertical padding per Figma. The <h3> carries margin:0 to
 * override UA heading defaults. */
.sectionHeader {
  margin: 0;
  padding: var(--spacing-12) var(--spacing-16);
}

/* Flat-list row — 48px min-height, 16px horizontal inset, 6px
 * vertical padding so the 28px TabGroup tabs / 20px lg Toggle
 * tracks center-align inside. `min-height` (not `height`) so a
 * wrapped label in a narrower locale expands the row instead of
 * clipping. justify-content: space-between pushes label left and
 * control right regardless of container width. */
/* 6px literal: tokens-figma.css has no --spacing-6 in its 2/4/8/12/16
 * scale (matches the Figma spacing token set). The Figma row spec
 * pins vertical padding to 6px specifically so the 28px TabGroup
 * tabs and 20px lg Toggle tracks center inside the 48px row with a
 * couple of px of flex slack. Using --spacing-4 or --spacing-8
 * would shift the control baseline by 2px. */
.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  padding: 6px var(--spacing-16);
  gap: var(--spacing-12);
  box-sizing: border-box;
}

/* Keep the label (first child of each row) from wrapping or
 * shrinking against the right-hand control. Carried over from
 * commit 6eb019e — load-bearing for long i18n labels (DE/FR
 * "Anrufbenachrichtigungen") inside the 360px popup. The rule
 * applies structurally to every present and future row
 * regardless of label content. */
.row > :first-child {
  white-space: nowrap;
  flex-shrink: 0;
}

/* `<label>` wrapping the notification-toggle Text. Cursor hints
 * the label is an interactive click-target for the sibling
 * Toggle (associated via htmlFor / id). */
.rowLabel {
  cursor: pointer;
}

/* Section divider — 8px neutral strip between sections. Not a
 * border because the Figma design shows breathing room, not a
 * hairline. Full-width so it spans the container edge-to-edge. */
.sectionDivider {
  height: var(--spacing-8);
  flex-shrink: 0;
}

/* Disconnect button area. 12px vertical + 16px horizontal padding
 * so the negative button sits inset from the row rhythm above. */
.disconnectArea {
  padding: var(--spacing-12) var(--spacing-16);
}

/* 10px literal: tokens-figma.css has no --spacing-10 in its
 * 2/4/8/12/16 scale. The confirm row needs a gap between the
 * "Are you sure?" copy and the Yes/Cancel button pair that sits
 * between --spacing-8 (too tight against the 32px button height)
 * and --spacing-12 (reads as a paragraph break). Kept as a
 * literal until design ships a --spacing-10 token. */
.confirmRow {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.confirmActions {
  display: flex;
  gap: var(--spacing-8);
}

/* Explicit flex:1 on both confirm buttons (carried over from the
 * previous layout) so the Yes/Cancel pair splits the confirm row
 * evenly. `width: 100%` on two flex children relies on default
 * flex-shrink to resolve — flex:1:1:0 is the structural
 * guarantee. */
.confirmAction {
  flex: 1;
}

/* Support row — inline `<Link>` with surrounding copy. 12px
 * vertical + 16px horizontal padding matches the row rhythm
 * while not being a toggleable row itself. */
.supportRow {
  padding: var(--spacing-12) var(--spacing-16);
}

/* Caption under a toggle row (currently the Privacy section's
 * analytics toggle) — same 16px horizontal inset as `.row`, with
 * 12px of breathing room below before the next section divider. */
.rowDescription {
  margin: 0;
  padding: 0 var(--spacing-16) var(--spacing-12);
}

/* Language picker — native select styled with the Input primitive's
 * token vocabulary. max-width guards the 360px popup against long
 * endonyms ("Português (Brasil)"); the row's space-between handles
 * the rest. */
.languageSelect {
  font: var(--text-body-medium);
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--stroke-neutral);
  border-radius: 8px;
  padding: 4px 8px;
  max-width: 200px;
}
